import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { useAdmin } from '@/hooks/useAdmin'
import { supabase } from '@/integrations/supabase/client'
import { useSponsorPricing } from '@/hooks/useSponsorPricing'
import {
    Users, BookOpen, TrendingUp, Clock, AlertCircle,
    CheckCircle, Loader2, Plus, Link, ChevronDown, ChevronUp,
    Send, X, Building2, RefreshCw,
} from 'lucide-react'
import SEO from '@/components/layout/SEO'
import CertificateGallery from '@/components/education/CertificateGallery'

// ── Types ────────────────────────────────────────────────────────────────────
interface OrgChild {
    id: string
    child_name: string
    child_age: number | null
    guardian_email: string | null
    status: string
    is_active: boolean
    activated_at: string | null
    expires_at: string | null
    assignment_id: string | null
    assigned_user_id?: string | null
    lessons_completed?: number
    mastery_score?: number
    quiz_avg_score?: number
    subjects_active?: number
    last_active_at?: string | null
}

interface OrgInfo {
    id: string
    org_name: string
    org_type: string
    org_country: string | null
    org_city: string | null
    org_contact_name: string | null
    seats_allocated: number
    seats_used: number
    plan_key: string | null
    sponsorship_id: string | null
    status: string
}

interface SeatRequest {
    id: string
    seats_requested: number
    reason: string | null
    status: string
    admin_note: string | null
    created_at: string | null
}

// ── Status badge ─────────────────────────────────────────────────────────────
function Badge({ status }: { status: string }) {
    return (
        <span className={`text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
            status === 'active' || status === 'approved' ? 'bg-emerald-100 text-emerald-300' :
            status === 'assigned' ? 'bg-blue-100 text-blue-800' :
            status === 'pending' ? 'bg-amber-100 text-amber-800' :
            status === 'expired' || status === 'declined' ? 'bg-red-100 text-red-800' :
            'bg-muted text-muted-foreground'
        }`}>{status}</span>
    )
}

// ── Mini progress bar ─────────────────────────────────────────────────────────
function MiniBar({ value, max = 100, color = 'bg-primary' }: { value: number; max?: number; color?: string }) {
    const pct = Math.min(100, max > 0 ? (value / max) * 100 : 0)
    return (
        <div className="h-1.5 bg-muted rounded-full overflow-hidden w-full">
            <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
        </div>
    )
}

// ── Request Seats Modal ───────────────────────────────────────────────────────
function RequestSeatsModal({ orgId, onClose, onSuccess }: {
    orgId: string; onClose: () => void; onSuccess: () => void
}) {
    const { t } = useTranslation()
    const [seats, setSeats] = useState(1)
    const [reason, setReason] = useState('')
    const [saving, setSaving] = useState(false)
    const [err, setErr] = useState('')

    async function submit() {
        if (!reason.trim()) { setErr(t('ngo.dashboard.requests.reasonRequired')); return }
        setSaving(true); setErr('')
        try {
            const { error } = await supabase.from('org_seat_requests').insert({
                org_id: orgId,
                requested_by: (await supabase.auth.getUser()).data.user?.id,
                seats_requested: seats,
                reason: reason.trim(),
            })
            if (error) throw error
            onSuccess()
        } catch (e: any) {
            setErr(e.message || t('ngo.dashboard.requests.failedToSend'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-6">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                className="bg-card rounded-3xl p-8 w-full max-w-md shadow-2xl">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-black text-foreground">{t('ngo.dashboard.requests.title')}</h3>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="mb-5">
                    <label className="block text-[11px] font-extrabold text-muted-foreground uppercase tracking-widest mb-2">
                        {t('ngo.dashboard.requests.seatsNeeded')}
                    </label>
                    <div className="flex items-center gap-4">
                        <button onClick={() => setSeats(s => Math.max(1, s - 1))}
                            className="w-10 h-10 rounded-full border-2 border-border bg-card text-xl font-bold flex items-center justify-center hover:border-primary transition-colors">−</button>
                        <span className="text-3xl font-black text-foreground min-w-[40px] text-center">{seats}</span>
                        <button onClick={() => setSeats(s => s + 1)}
                            className="w-10 h-10 rounded-full border-2 border-border bg-card text-xl font-bold flex items-center justify-center hover:border-primary transition-colors">+</button>
                    </div>
                </div>

                <div className="mb-5">
                    <label className="block text-[11px] font-extrabold text-muted-foreground uppercase tracking-widest mb-2">
                        {t('ngo.dashboard.requests.reasonLabel')}
                    </label>
                    <textarea
                        value={reason} onChange={e => setReason(e.target.value)}
                        placeholder={t('ngo.dashboard.requests.reasonPlaceholder')}
                        rows={4}
                        className={`w-full p-3 rounded-xl border-2 ${err ? 'border-destructive' : 'border-border'} text-sm resize-vertical font-inherit outline-none focus:border-primary transition-colors`}
                    />
                    {err && <p className="text-destructive text-xs mt-1.5 font-semibold">{err}</p>}
                </div>

                <div className="flex gap-3">
                    <button onClick={submit} disabled={saving}
                        className="flex-1 py-3 rounded-xl bg-primary text-primary-foreground font-extrabold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors hover:bg-primary/90">
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        {saving ? t('ngo.dashboard.requests.sending') : t('ngo.dashboard.requests.sendRequest')}
                    </button>
                    <button onClick={onClose}
                        className="py-3 px-5 rounded-xl bg-muted border border-border text-muted-foreground font-bold hover:bg-accent transition-colors">
                        {t('ngo.dashboard.requests.cancel')}
                    </button>
                </div>
            </motion.div>
        </div>
    )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function NgoDashboardPage() {
    const { user, isLoading: authLoading } = useAuth()
    const { isAdmin } = useAdmin()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const sponsorPricing = useSponsorPricing()

    const [org, setOrg] = useState<OrgInfo | null>(null)
    const [children, setChildren] = useState<OrgChild[]>([])
    const [seatRequests, setSeatRequests] = useState<SeatRequest[]>([])
    const [loading, setLoading] = useState(true)
    const [noOrg, setNoOrg] = useState(false)
    const [expandedChild, setExpandedChild] = useState<string | null>(null)
    const [showRequestModal, setShowRequestModal] = useState(false)
    const [requestSuccess, setRequestSuccess] = useState(false)
    const [filter, setFilter] = useState<'all' | 'active' | 'pending' | 'expired'>('all')
        const [showAddChild, setShowAddChild] = useState(false)
    const [ncName, setNcName] = useState(''); const [ncAge, setNcAge] = useState('')
    const [ncGuardianEmail, setNcGuardianEmail] = useState('')
    const [ncLoading, setNcLoading] = useState(false)
    const [buySeats, setBuySeats] = useState(1); const [buyLoading, setBuyLoading] = useState(false)
    const [activatingChild, setActivatingChild] = useState<string | null>(null)

    async function addChild() {
        if (!org || !ncName.trim() || !ncGuardianEmail.trim()) return
        setNcLoading(true)
        try {
            const { error } = await supabase.from('org_nomination_children').insert({
                org_id: org.id, child_name: ncName.trim(),
                child_age: ncAge ? parseInt(ncAge) : null,
                guardian_email: ncGuardianEmail.trim().toLowerCase(),
                status: 'pending', is_active: false,
            })
            if (error) throw error
            setShowAddChild(false); setNcName(''); setNcAge(''); setNcGuardianEmail('')
            load()
        } catch (e: any) { alert(e.message) } finally { setNcLoading(false) }
    }

    async function buyOwnSeats() {
        if (!org || !user) return
        setBuyLoading(true)
        try {
            const { data, error } = await supabase.functions.invoke('billing', {
                body: {
                    action: 'checkout', plan: 'sponsor_seat_monthly', interval: 'month',
                    quantity: buySeats, destination: 'ngo_seat',
                    metadata: { org_id: org.id, sponsor_type: 'ngo', is_self_managed: 'true' },
                },
            })
            if (error || !data?.url) throw new Error(error?.message || data?.error || 'Checkout failed')
            window.location.href = data.url
        } catch (e: any) { alert(e.message) } finally { setBuyLoading(false) }
    }

    async function activateChild(childId: string) {
        if (!org?.sponsorship_id) return
        setActivatingChild(childId)
        try {
            const { data, error } = await supabase.functions.invoke('assign-sponsor-seat', {
                body: { source: 'ngo_self', sponsorship_id: org.sponsorship_id, org_id: org.id, child_id: childId },
            })
            if (error || data?.error) throw new Error(error?.message || data?.error)
            load()
        } catch (e: any) { alert(e.message) } finally { setActivatingChild(null) }
    }

    const load = useCallback(async () => {
        if (!user) return
        setLoading(true)
        try {
            let { data: orgData } = await supabase
                .from('org_nominations')
                .select('id, org_name, org_type, org_country, org_city, org_contact_name, org_contact_email, contact_user_id, sponsorship_id, seats_allocated, seats_used, plan_key, status')
                .or(`contact_user_id.eq.${user.id},org_contact_email.eq.${user.email}`)
                .limit(1)
                .maybeSingle()

            if (!orgData && isAdmin) {
                const { data: adminOrg } = await supabase
                    .from('org_nominations')
                    .select('id, org_name, org_type, org_country, org_city, org_contact_name, org_contact_email, contact_user_id, sponsorship_id, seats_allocated, seats_used, plan_key, status')
                    .order('created_at', { ascending: false })
                    .limit(1)
                    .maybeSingle()
                if (adminOrg) orgData = adminOrg
            }

            if (!orgData) { setNoOrg(true); setLoading(false); return }

// (removed — no UPDATE policy for contacts; email-match carries all access post-Fix-1)

            setOrg(orgData)

            const { data: childData } = await supabase
                .from('org_nomination_children')
                .select(`
          id, child_name, child_age, guardian_email, status, is_active,
          activated_at, expires_at, assignment_id,
          sponsor_seat_assignments!org_nomination_children_assignment_id_fkey(
            assigned_user_id
          )
        `)
                .eq('org_id', orgData.id)
                .order('created_at', { ascending: false })

                        // Batched: one snapshot query for all learners, latest-per-user picked client-side
            const assignedIds = [...new Set((childData || [])
                .map((c: any) => c.sponsor_seat_assignments?.assigned_user_id)
                .filter(Boolean))] as string[]
            const latestSnap: Record<string, any> = {}
            if (assignedIds.length > 0) {
                const { data: snaps } = await supabase
                    .from('sponsor_progress_snapshots')
                    .select('*')
                    .in('assigned_user_id', assignedIds)
                    .order('snapshot_date', { ascending: false })
                for (const s of snaps ?? []) {
                    if (s.assigned_user_id && !latestSnap[s.assigned_user_id]) latestSnap[s.assigned_user_id] = s
                }
                // On-demand sync for active learners with no snapshot yet (capped)
                const missing = assignedIds.filter(id => !latestSnap[id]).slice(0, 10)
                await Promise.all(missing.map(id =>
                    supabase.functions.invoke('sync-sponsor-snapshots', { body: { assigned_user_id: id } }).catch(() => {})))
                if (missing.length > 0) {
                    const { data: snaps2 } = await supabase
                        .from('sponsor_progress_snapshots').select('*')
                        .in('assigned_user_id', missing).order('snapshot_date', { ascending: false })
                    for (const s of snaps2 ?? []) {
                        if (s.assigned_user_id && !latestSnap[s.assigned_user_id]) latestSnap[s.assigned_user_id] = s
                    }
                }
            }

            const enriched: OrgChild[] = (childData || []).map((c: any) => {
                const assignedUserId = c.sponsor_seat_assignments?.assigned_user_id ?? null
                const progress = assignedUserId ? latestSnap[assignedUserId] ?? null : null
                return {
                    id: c.id, child_name: c.child_name, child_age: c.child_age,
                    guardian_email: c.guardian_email, status: c.status, is_active: c.is_active,
                    activated_at: c.activated_at, expires_at: c.expires_at,
                    assignment_id: c.assignment_id, assigned_user_id: assignedUserId,
                    lessons_completed: progress?.lessons_completed ?? 0,
                    mastery_score: progress?.mastery_score ?? 0,
                    quiz_avg_score: progress?.quiz_avg_score ?? 0,
                    subjects_active: progress?.subjects_active ?? 0,
                    last_active_at: progress?.last_active_at ?? null,
                }
            })
            setChildren(enriched)

            const { data: requests } = await supabase
                .from('org_seat_requests')
                .select('id, seats_requested, reason, status, admin_note, created_at')
                .eq('org_id', orgData.id)
                .order('created_at', { ascending: false })
            setSeatRequests(requests || [])

        } catch (err) {
            console.error('NGO dashboard load error:', err)
        } finally {
            setLoading(false)
        }
    }, [user?.id])

    useEffect(() => {
        if (!authLoading && !user?.id) {
            navigate('/auth/login?next=/ngo/dashboard')
        }
    }, [user?.id, authLoading, navigate])

    useEffect(() => { if (user) load() }, [user?.id, load])

    if (authLoading || loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 size={36} className="animate-spin text-primary" />
            </div>
        )
    }

    if (!user) return null

    if (noOrg) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-6">
                <SEO title={t('ngo.dashboard.title')} noindex />
                <div className="text-center max-w-md space-y-4">
                    <Building2 size={48} className="text-muted-foreground mx-auto" />
                    <h2 className="text-2xl font-black text-foreground">{t('ngo.dashboard.noOrgTitle')}</h2>
                    <p className="text-muted-foreground leading-relaxed">
                        {t('ngo.dashboard.noOrgDesc')}
                    </p>
                    <Link to="/how-it-works" className="text-sm font-bold text-emerald-400 hover:text-emerald-300 mb-4 inline-block">
                        How the partner program works →
                    </Link>
                    <div>
                        <Link to="/ngo/apply"
                            className="inline-block px-7 py-3 bg-primary text-primary-foreground rounded-xl font-extrabold no-underline hover:bg-primary/90 transition-colors">
                            Apply for Partner Status
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    if (!org) return null

    const activeCount = children.filter(c => c.is_active).length
    const pendingCount = children.filter(c => c.status === 'pending').length
    const expiredCount = children.filter(c => c.status === 'expired' || (c.expires_at && new Date(c.expires_at) < new Date())).length
    const seatsAvailable = org.seats_allocated - org.seats_used
    const pendingRequests = seatRequests.filter(r => r.status === 'pending')

    const filtered = filter === 'all' ? children
        : filter === 'active' ? children.filter(c => c.is_active)
            : filter === 'pending' ? children.filter(c => c.status === 'pending' || c.status === 'assigned')
                : children.filter(c => c.status === 'expired' || (c.expires_at && new Date(c.expires_at) < new Date()))

    const filterKeys: ('all' | 'active' | 'pending' | 'expired')[] = ['all', 'active', 'pending', 'expired']

    return (
        <div className="min-h-screen bg-background">
            <SEO title={`${org.org_name}: ${t('ngo.dashboard.title')}`} noindex />

            {/* Hero */}
            <div className="bg-primary text-primary-foreground">
                <div className="max-w-5xl mx-auto px-6 pt-8 pb-6">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
                        <div>
                            <div className="text-[11px] font-extrabold text-primary-foreground/60 uppercase tracking-widest mb-1">
                                {t('ngo.dashboard.portalLabel')}
                            </div>
                            <h1 className="text-2xl font-black text-white">{org.org_name}</h1>
                            <p className="text-sm text-primary-foreground/50 mt-1">
                                {org.org_type.replace(/_/g, ' ')} · {[org.org_city, org.org_country].filter(Boolean).join(', ')}
                            </p>
                        </div>
                        <div className="flex gap-2.5">
                            <button onClick={() => load()}
                                className="px-4 py-2 bg-primary-foreground/10 border border-primary-foreground/20 rounded-xl text-primary-foreground flex items-center gap-1.5 text-sm font-semibold hover:bg-primary-foreground/20 transition-colors">
                                <RefreshCw size={14} /> {t('ngo.dashboard.refresh')}
                            </button>
                            <button onClick={() => setShowAddChild(true)}
                                className="px-4 py-2 bg-primary-foreground/10 border border-primary-foreground/20 rounded-xl text-primary-foreground flex items-center gap-1.5 text-sm font-semibold hover:bg-primary-foreground/20 transition-colors">
                                <Plus size={14} /> Add Child
                            </button>
                            <button onClick={() => setShowRequestModal(true)}
                                className="px-4 py-2 bg-accent text-accent-foreground rounded-xl flex items-center gap-1.5 text-sm font-extrabold hover:bg-accent/90 transition-colors">
                                <Plus size={14} /> {t('ngo.dashboard.requestSeats')}
                            </button>
                        </div>
                    </div>

                    {/* Stats row */}
                    <div className="flex gap-3 flex-wrap">
                        {[
                            { label: t('ngo.dashboard.stats.totalMembers'), value: children.length, icon: <Users size={18} /> },
                            { label: t('ngo.dashboard.stats.activeSeats'), value: activeCount, icon: <CheckCircle size={18} /> },
                            { label: t('ngo.dashboard.stats.awaitingAssignment'), value: pendingCount, icon: <Clock size={18} /> },
                            { label: t('ngo.dashboard.stats.seatsAvailable'), value: seatsAvailable, icon: <TrendingUp size={18} /> },
                            { label: t('ngo.dashboard.stats.expired'), value: expiredCount, icon: <AlertCircle size={18} /> },
                        ].map(s => (
                            <div key={s.label} className="bg-primary-foreground/10 rounded-2xl px-5 py-4 border border-primary-foreground/10 flex-1 min-w-[140px]">
                                <div className="text-primary-foreground/70 mb-2.5">{s.icon}</div>
                                <p className="text-3xl font-black text-primary-foreground">{s.value}</p>
                                <p className="text-[10px] font-extrabold text-primary-foreground/45 uppercase tracking-widest mt-1">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 py-6 space-y-5">

                {/* Pending seat request notices */}
                {pendingRequests.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200/40 rounded-2xl px-4 py-3 flex items-center gap-2.5">
                        <Clock size={18} className="text-amber-500 flex-shrink-0" />
                        <p className="text-sm font-bold text-amber-800">
                            {t('ngo.dashboard.pendingRequestNotice', { count: pendingRequests.length })}
                        </p>
                    </div>
                )}

                {/* Seat allocation bar */}
                <div className="bg-card rounded-2xl p-5 border border-border">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="font-extrabold text-foreground text-sm">{t('ngo.dashboard.seatAllocation')}</h3>
                        <span className="text-sm font-bold text-primary">{org.seats_used} / {org.seats_allocated} {t('ngo.dashboard.used')}</span>
                    </div>
                    <MiniBar value={org.seats_used} max={org.seats_allocated} color={org.seats_used >= org.seats_allocated ? 'bg-destructive' : 'bg-primary'} />
                    <p className="text-xs text-muted-foreground mt-2">
                        {t('ngo.dashboard.plan')}: <strong className="text-foreground">{org.plan_key ?? 'Standard'}</strong>
                        {seatsAvailable === 0 && (
                            <span className="text-destructive font-bold ml-3">⚠ {t('ngo.dashboard.allSeatsWarning')}</span>
                        )}
                    </p>
                                        {org.sponsorship_id && (
                        <div className="mt-3 space-y-2">
                            <div className="flex items-center justify-between gap-3 bg-muted rounded-xl p-3">
                                <p className="text-[11px] text-muted-foreground leading-snug">{t('ngo.add_seats_to_your_allocation_billed')}</p>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => setBuySeats(s => Math.max(1, s - 1))} className="w-6 h-6 rounded-full border border-border text-xs font-bold">−</button>
                                        <span className="text-xs font-black w-4 text-center">{buySeats}</span>
                                        <button onClick={() => setBuySeats(s => s + 1)} className="w-6 h-6 rounded-full border border-border text-xs font-bold">+</button>
                                    </div>
                                </div>
                            </div>
                            <button onClick={buyOwnSeats} disabled={buyLoading}
                                className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-extrabold flex items-center justify-center gap-2">
                                {buyLoading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                Purchase {buySeats} {buySeats === 1 ? 'seat' : 'seats'} · ${sponsorPricing.standard.month.toFixed(2)}/seat/mo
                            </button>
                        </div>
                    )}
                    {!org.sponsorship_id && (
                        <div className="mt-3 flex items-center justify-between gap-3 bg-muted rounded-xl p-3">
                            <p className="text-[11px] text-muted-foreground leading-snug">{t('ngo.need_seats_now_purchase_instantly_at')}</p>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <div className="flex items-center gap-1">
                                    <button onClick={() => setBuySeats(s => Math.max(1, s - 1))} className="w-6 h-6 rounded-full border border-border text-xs font-bold">−</button>
                                    <span className="text-xs font-black w-4 text-center">{buySeats}</span>
                                    <button onClick={() => setBuySeats(s => s + 1)} className="w-6 h-6 rounded-full border border-border text-xs font-bold">+</button>
                                </div>
                                <button onClick={buyOwnSeats} disabled={buyLoading}
                                    className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[11px] font-extrabold">
                                    {buyLoading ? '…' : 'Buy'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Member list */}
                <div className="bg-card rounded-2xl border border-border overflow-hidden">
                    <div className="px-5 pt-5 flex justify-between items-center flex-wrap gap-3 mb-4">
                        <h3 className="font-extrabold text-foreground text-sm">{t('ngo.dashboard.membersProgress')}</h3>
                        <div className="flex gap-1 bg-muted rounded-xl p-1">
                            {filterKeys.map(f => (
                                <button key={f} onClick={() => setFilter(f)}
                                    className={`px-3.5 py-1.5 rounded-lg text-xs font-bold capitalize transition-all ${
                                        filter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                                    }`}>
                                    {t(`ngo.dashboard.filters.${f}`)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Users size={36} className="opacity-25 mx-auto mb-3" />
                            <p className="font-semibold">{t('ngo.dashboard.noMembers')}</p>
                        </div>
                    ) : (
                        <div>
                            {filtered.map((child, idx) => {
                                const isOpen = expandedChild === child.id
                                const hasProgress = child.assigned_user_id && (child.subjects_active ?? 0) > 0
                                const expiredSoon = child.expires_at && !child.status.includes('expired')
                                    && (new Date(child.expires_at).getTime() - Date.now()) < 1000 * 60 * 60 * 24 * 30

                                return (
                                    <div key={child.id} className={idx > 0 ? 'border-t border-border' : ''}>
                                        <div className="px-5 py-3.5 flex items-center gap-4 cursor-pointer hover:bg-accent/30 transition-colors"
                                            onClick={() => setExpandedChild(isOpen ? null : child.id)}>

                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${child.is_active ? 'bg-primary/10' : 'bg-muted'}`}>
                                                <span className={`text-sm font-extrabold ${child.is_active ? 'text-primary' : 'text-muted-foreground'}`}>
                                                    {child.child_name.charAt(0).toUpperCase()}
                                                </span>
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-bold text-sm text-foreground">{child.child_name}</span>
                                                    {child.child_age && <span className="text-xs text-muted-foreground">{t('ngo.dashboard.age')} {child.child_age}</span>}
                                                    <Badge status={child.is_active ? 'active' : child.status} />
                                                    {expiredSoon && <span className="text-[10px] font-bold text-amber-500">⚠ {t('ngo.dashboard.expiringSoon')}</span>}
                                                </div>
                                                {child.guardian_email && (
                                                    <p className="text-[11px] text-muted-foreground mt-0.5">{child.guardian_email}</p>
                                                )}
                                            </div>

                                            {hasProgress && (
                                                <div className="text-right min-w-[80px]">
                                                    <p className="text-lg font-black text-foreground">{child.mastery_score}%</p>
                                                    <p className="text-[10px] text-muted-foreground font-bold">{t('ngo.dashboard.mastery')}</p>
                                                </div>
                                            )}

                                            {isOpen ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
                                        </div>

                                        <AnimatePresence>
                                            {isOpen && (
                                                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                                                    className="border-t border-border overflow-hidden">
                                                    <div className="px-5 py-4 bg-muted/50">
                                                        {child.is_active && child.assigned_user_id ? (
                                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                                                {[
                                                                    { label: t('ngo.dashboard.subjectsActive'), value: child.subjects_active ?? 0 },
                                                                    { label: t('ngo.dashboard.lessonsCompleted'), value: child.lessons_completed ?? 0 },
                                                                    { label: t('ngo.dashboard.quizAverage'), value: `${child.quiz_avg_score ?? 0}%` },
                                                                    { label: t('ngo.dashboard.masteryScore'), value: `${child.mastery_score ?? 0}%` },
                                                                ].map(stat => (
                                                                    <div key={stat.label} className="bg-card rounded-xl p-3.5 border border-border">
                                                                        <p className="text-[11px] font-extrabold text-muted-foreground uppercase tracking-widest mb-1.5">{stat.label}</p>
                                                                        <p className="text-xl font-black text-foreground">{stat.value}</p>
                                                                    </div>
                                                                ))}
                                                                {child.assigned_user_id && (
                                                                    <div className="col-span-full">
                                                                        <CertificateGallery userId={child.assigned_user_id} compact />
                                                                    </div>
                                                                )}
                                                                {child.mastery_score !== undefined && (
                                                                    <div className="col-span-full">
                                                                        <p className="text-[11px] font-bold text-muted-foreground mb-1.5">{t('ngo.dashboard.overallMastery')}</p>
                                                                        <MiniBar value={child.mastery_score}
                                                                            color={child.mastery_score >= 80 ? 'bg-emerald-500' : child.mastery_score >= 50 ? 'bg-amber-500' : 'bg-destructive'} />
                                                                    </div>
                                                                )}
                                                                {child.last_active_at && (
                                                                    <p className="text-xs text-muted-foreground col-span-full">
                                                                        {t('ngo.dashboard.lastActive')}: {new Date(child.last_active_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                                    </p>
                                                                )}
                                                                {child.expires_at && (
                                                                    <p className={`text-xs col-span-full ${expiredSoon ? 'text-amber-500 font-bold' : 'text-muted-foreground'}`}>
                                                                        {t('ngo.dashboard.seatExpires')}: {new Date(child.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ) : child.assignment_id ? (
                                                            <p className="text-sm text-muted-foreground italic">{t('ngo.dashboard.pendingActivation')}</p>
                                                        ) : (
                                                            <div className="flex items-center gap-2.5">
                                                                <AlertCircle size={16} className="text-amber-500 flex-shrink-0" />
                                                                <p className="text-sm text-foreground">
                                                                    <strong>{t('ngo.dashboard.noSeatAssigned')}</strong> {t('ngo.dashboard.noSeatAssignedDesc')}
                                                                </p>
                                                                                                                                {org.sponsorship_id && (
                                                                    <button onClick={() => activateChild(child.id)} disabled={activatingChild === child.id}
                                                                        className="ml-3 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[11px] font-extrabold flex-shrink-0">
                                                                        {activatingChild === child.id ? 'Activating…' : 'Activate Seat'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>

                {/* Seat request history */}
                {seatRequests.length > 0 && (
                    <div className="bg-card rounded-2xl border border-border p-5">
                        <h3 className="font-extrabold text-foreground text-sm mb-4">{t('ngo.dashboard.requests.history')}</h3>
                        <div className="flex flex-col gap-2.5">
                            {seatRequests.map(req => (
                                <div key={req.id} className="flex items-center justify-between flex-wrap gap-2 px-4 py-3 bg-muted rounded-xl">
                                    <div>
                                        <span className="font-bold text-foreground text-sm">+{req.seats_requested} {req.seats_requested > 1 ? t('ngo.dashboard.requests.seatsCount') : t('ngo.dashboard.requests.seatsCount')}</span>
                                        {req.reason && <p className="text-xs text-muted-foreground mt-0.5">{req.reason}</p>}
                                        {req.admin_note && <p className="text-xs text-primary mt-0.5 font-semibold">{t('ngo.dashboard.requests.adminNote')}: {req.admin_note}</p>}
                                    </div>
                                    <div className="flex items-center gap-2.5">
                                        <span className="text-[11px] text-muted-foreground">
                                            {req.created_at ? new Date(req.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : t('ngo.dashboard.requests.status.pending')}
                                        </span>
                                        <Badge status={req.status} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Contact info */}
                <div className="bg-card rounded-2xl border border-border p-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        {t('ngo.dashboard.contactHelp')}{' '}
                        <a href="mailto:support@stillwatersacademy.com" className="text-primary font-bold">support@stillwatersacademy.com</a>.{' '}
                        {t('ngo.dashboard.contactHelpSuffix')}
                    </p>
                </div>
            </div>

                        {/* Add child modal */}
            {showAddChild && (
                <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-6">
                    <div className="bg-card rounded-3xl p-8 w-full max-w-md shadow-2xl">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-black text-foreground">{t('ngo.nominate_learner')}</h3>
                            <button onClick={() => setShowAddChild(false)} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-[11px] font-extrabold text-muted-foreground uppercase tracking-widest mb-1.5">{t('ngo.child_name')}</label>
                                <input value={ncName} onChange={e => setNcName(e.target.value)} className="w-full px-4 py-3 rounded-xl border-2 border-border text-sm outline-none focus:border-primary" placeholder="e.g. Maria Santos" />
                            </div>
                            <div>
                                <label className="block text-[11px] font-extrabold text-muted-foreground uppercase tracking-widest mb-1.5">Age</label>
                                <input value={ncAge} onChange={e => setNcAge(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-3 rounded-xl border-2 border-border text-sm outline-none focus:border-primary" placeholder={t('ngo.11')} />
                            </div>
                            <div>
                                <label className="block text-[11px] font-extrabold text-muted-foreground uppercase tracking-widest mb-1.5">{t('ngo.guardian_email')}</label>
                                <input type="email" value={ncGuardianEmail} onChange={e => setNcGuardianEmail(e.target.value)} className="w-full px-4 py-3 rounded-xl border-2 border-border text-sm outline-none focus:border-primary" placeholder={t('ngo.guardian_email_com_the_seat_invitation')} />
                            </div>
                            <button onClick={addChild} disabled={ncLoading || !ncName.trim() || !ncGuardianEmail.trim()}
                                className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-extrabold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                                {ncLoading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Nominate Learner
                            </button>
                            <p className="text-[11px] text-muted-foreground text-center">The child appears as pending. Seats reach them via sponsorship assignment or your purchase; their invitation email goes to the guardian.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Request seats modal */}
            {showRequestModal && (
                <RequestSeatsModal
                    orgId={org.id}
                    onClose={() => setShowRequestModal(false)}
                    onSuccess={() => {
                        setShowRequestModal(false)
                        setRequestSuccess(true)
                        load()
                        setTimeout(() => setRequestSuccess(false), 4000)
                    }}
                />
            )}

            {/* Success toast */}
            <AnimatePresence>
                {requestSuccess && (
                    <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
                        className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground rounded-2xl px-6 py-3.5 font-bold text-sm shadow-xl flex items-center gap-2 z-50">
                        <CheckCircle size={18} /> {t('ngo.dashboard.requests.success')}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
